import { Billables } from '@domains/subscription/priv/Billables/usecases/BillableList/Billables'
import type { EditTemplateFormData } from '@domains/subscription/priv/TemplateDetails/EditTemplate'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import useMaterialNotification from 'hooks/useMaterialNotification'
import { useCallback } from 'react'
import {
  QueryKey,
  useAvaliableFeatures,
  useBillables,
  useCreateTemplateOptions,
  useDeleteBillableItem,
  useDeleteTemplate,
  useEditTemplate,
  useGetMergedTemplate,
} from '../api/queries'
import { type ConfirmableProps, ConfirmModal, type FieldChange } from '../components/Confirmable'
import { useSaveFeatureForTemplate } from '../Features/api/queries'
import { FeatureForm as FeatureFormComponent, type FeatureFormProps } from '../Features/FeatureForm'
import { Features } from '../Features/Features'
import { TemplateDetails, TemplateTabs } from '../TemplateDetails/TemplateDetails'
import type { AnchorablePayment, BillablePrice, FeatureRequest, FieldRequest, FixedPayment, PaymentFrequencyRequest } from '../types'

export interface Navigation {
  back: () => void
  tab: (tab: string) => void
  featureDetails: (featureId: string) => void
  billableDetails: (billableId: string) => void
}

type FormFieldRequest = FieldRequest<null>

interface TemplateDetailsAdapterProps {
  templateId: string
  tab: string
  id: string
  navigation: Navigation
}

export const TemplateDetailsFactory = ({ templateId, id, tab, navigation }: TemplateDetailsAdapterProps) => {
  const notify = useMaterialNotification()

  const { data: options } = useCreateTemplateOptions()

  const { template, isLoading, avaliableFeatures, billables, saveFeature, deleteTemplate, updateTemplate, deleteBillable, invalidate } =
    useTemplateDetails({
      templateId,
      onNotification: notify,
    })

  const handleDeleteTemplate = (templateId: string) => {
    deleteTemplate(templateId, {
      onSuccess: () => {
        notify('Template has been deleted')
        navigation.back()
      },
    })
  }

  const handleUpdateTemplate = async (data: EditTemplateFormData) => {
    await updateTemplate({
      eligibility: data.eligibility,
      externalName: data.externalName,
      name: data.name,
    })
  }

  const ConfirmableComponent = useCallback(
    (props: ConfirmableProps<FormFieldRequest[]>) => {
      if (!template?.numberOfConnections) return null

      return <ConfirmModal {...props} connections={template.numberOfConnections} />
    },
    [template?.numberOfConnections],
  )

  const FeatureForm = useCallback(
    (props: FeatureFormProps) => (
      <FeatureFormComponent {...props} Confirmable={showConfirmable(template?.numberOfConnections, ConfirmableComponent)} />
    ),
    [ConfirmableComponent, template?.numberOfConnections],
  )

  if (!template) {
    return null
  }

  return (
    <TemplateDetails
      template={template}
      navigateBack={navigation.back}
      onTabChange={navigation.tab}
      tab={mapTabToTemplateTab(tab, navigation.tab)}
      onDeleteTemplate={handleDeleteTemplate}
      onEditTemplate={handleUpdateTemplate}
      options={options}
      slots={{
        FeaturesTab: (
          <Features
            availableFeatures={avaliableFeatures}
            activeFeatures={template.features}
            onSave={saveFeature}
            isLoading={isLoading}
            selectedFeatureId={id}
            onSelectFeature={navigation.featureDetails}
            isFormDirty={deepEqualFormDirty}
            slots={{ FeatureForm }}
          />
        ),
        BillablesTab: (
          <Billables
            template={template}
            availableBillables={billables}
            onDelete={deleteBillable}
            onNavigateToBillable={navigation.billableDetails}
            selectedBillableId={id}
            onCreatedPrice={invalidate}
            formatBillingFrequency={formatBillingFrequency}
            currencies={options?.currencies ?? []}
          />
        ),
      }}
    />
  )
}

const showConfirmable = <T,>(numberOfConnections: number | undefined, Component: React.ComponentType<T>) => {
  if (numberOfConnections === undefined || numberOfConnections <= 0) {
    return undefined
  }

  return (props: T) => <Component {...props} connections={numberOfConnections} />
}

const formatBillingFrequency = (paymentFrequency: PaymentFrequencyRequest) => {
  switch (paymentFrequency.type) {
    case 'fixed': {
      return formatFixedBillingFrequency(paymentFrequency)
    }

    case 'anchorable': {
      return formatAnchoredBillingFrequency(paymentFrequency)
    }
  }
}

const formatAnchoredBillingFrequency = (anchoredPaymentFrequency: AnchorablePayment) => {
  switch (anchoredPaymentFrequency.period) {
    case 'weekly': {
      return `weekly`
    }

    case 'monthly': {
      return `monthly`
    }

    case 'yearly': {
      return `yearly`
    }
  }
}

const formatFixedBillingFrequency = (fixedPaymentFrequency: FixedPayment) => {
  switch (fixedPaymentFrequency.frequency) {
    case 'daily': {
      return 'daily'
    }

    case 'monthly': {
      return `monthly on the ${dayjs().date(fixedPaymentFrequency.dayOfMonth).format('Do')}`
    }

    case 'weekly': {
      return `weekly on ${fixedPaymentFrequency.dayOfWeek}`
    }

    case 'yearly': {
      return `yearly on ${dayjs()
        .startOf('year')
        .add(fixedPaymentFrequency.dayOfYear - 1, 'day')
        .format('Do [day of] MMMM')}`
    }
  }
}

const mapTabToTemplateTab = (tab: string, fallback: (tab: TemplateTabs) => void): TemplateTabs => {
  const defaultTab = TemplateTabs.FEATURES

  switch (tab) {
    case 'features':
      return TemplateTabs.FEATURES
    case 'billables':
      return TemplateTabs.BILLABLES
    default:
      fallback(defaultTab)
      return defaultTab
  }
}

const deepEqualFormDirty = (change: FieldChange) => {
  return JSON.stringify(change.before) !== JSON.stringify(change.after)
}

interface UseTemplateDetailsProps {
  templateId: string
  onNotification?: (message: string) => void
}

export const useTemplateDetails = ({ templateId, onNotification }: UseTemplateDetailsProps) => {
  const saveFeatureForTemplate = useSaveFeatureForTemplate()
  const avaliableFeatures = useAvaliableFeatures(templateId)
  const { data: template, isLoading: isLoadingTemplate, isFetching: isFetchingTemplate } = useGetMergedTemplate(templateId)
  const { data: billables = [], isLoading: isLoadingBillables, isFetching: isFetchingBillables } = useBillables()
  const { mutate: deleteBillable } = useDeleteBillableItem(templateId)
  const { mutate: deleteTemplate } = useDeleteTemplate()
  const { mutateAsync: updateTemplate } = useEditTemplate(templateId)

  const queryClient = useQueryClient()

  const saveFeature = (featureData: FeatureRequest<null>) => {
    saveFeatureForTemplate.mutate(
      { templateId, featureRequest: featureData },
      {
        onSuccess: () => {
          onNotification?.(`${featureData.name} has been saved`)
        },
      },
    )
  }

  const handleDeleteBillable = (billableId: string) => {
    deleteBillable(billableId, {
      onSuccess: () => {
        onNotification?.(`Billable item has been deleted`)
      },
    })
  }

  const handleUpdateTemplate = async (data: EditTemplateFormData) => {
    await updateTemplate({
      eligibility: data.eligibility,
      externalName: data.externalName,
      name: data.name,
    }).then(() => {
      onNotification?.('Template has been updated')
    })
  }

  const invalidate = () => {
    onNotification?.(`Price has been saved`)

    queryClient.invalidateQueries({ queryKey: QueryKey.Prices(templateId) })
    queryClient.invalidateQueries({ queryKey: QueryKey.Template(templateId) })
  }

  const isLoading = saveFeatureForTemplate.isLoading || isLoadingTemplate || isLoadingBillables || isFetchingTemplate || isFetchingBillables

  return {
    template,
    isLoading,
    avaliableFeatures,
    billables,
    saveFeature,
    deleteBillable: handleDeleteBillable,
    invalidate,
    deleteTemplate,
    updateTemplate: handleUpdateTemplate,
  }
}
